import { motion } from 'framer-motion';

const chapters = [
  {
    num: '01',
    title: 'Speed is a moat.',
    body: 'Banks measure risk in months. We measure opportunity in hours. Approvals inside 3, funding inside 24. When markets shift, our operators are already there.',
  },
  {
    num: '02',
    title: 'Discretion by design.',
    body: 'No committees. No leaks. Every file is worked by two principals, encrypted end-to-end, and shredded on close. Your growth is not a case study.',
  },
  {
    num: '03',
    title: 'Capital that flexes.',
    body: 'Rigid amortization kills good businesses. Our terms bend around seasonality, receivables, and expansion — because we underwrite operators, not spreadsheets.',
  },
  {
    num: '04',
    title: 'Operators, not analysts.',
    body: 'Every principal at Scorpion has built and exited an operating business. We speak your language because we have lived your quarter.',
  },
];

export default function Manifesto() {
  return (
    <section
      id="manifesto"
      className="relative bg-[#0a0a0a] py-28 lg:py-40 border-y border-[#c2a978]/10"
      data-testid="manifesto-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-24 lg:mb-32">
          <div className="lg:col-span-4 flex items-start gap-4">
            <span className="w-10 h-px bg-[#c2a978] mt-3" />
            <span className="text-[10px] tracking-[0.5em] uppercase text-[#c2a978]">
              Ch. 02 — Manifesto
            </span>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-8 font-serif text-3xl lg:text-5xl leading-[0.95] tracking-[-0.02em] text-[#f5f5f0]"
          >
            Four principles.<br />
            Zero <span className="italic text-[#c2a978]">apologies</span>.
          </motion.h2>
        </div>

        {/* Two-column: image + chapters */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-start">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 lg:sticky lg:top-32"
          >
            <div className="clip-frame aspect-[4/5] gold-glow">
              <img
                src="https://images.unsplash.com/photo-1718209881007-c0ecdfc00f9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxjb25maWRlbnQlMjBzdWNjZXNzZnVsJTIwYnVzaW5lc3MlMjBmb3VuZGVyJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg0MDQ4OTQzfDA&ixlib=rb-4.1.0&q=85"
                alt="Founder portrait"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="mt-6 flex items-start justify-between">
              <div>
                <div className="text-[10px] tracking-[0.35em] uppercase text-[#c2a978] mb-1">
                  Marcus Vale
                </div>
                <div className="text-[10px] tracking-[0.35em] uppercase text-[#8a8a8a]">
                  Managing Principal
                </div>
              </div>
              <span className="font-serif italic text-[#c2a978] text-4xl">✦</span>
            </div>
          </motion.div>

          {/* Chapters */}
          <div className="lg:col-span-7 space-y-16 lg:space-y-24">
            {chapters.map((c, i) => (
              <motion.div
                key={c.num}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.9, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="grid grid-cols-12 gap-4 lg:gap-8"
                data-testid={`manifesto-chapter-${i}`}
              >
                <div className="col-span-3 lg:col-span-2">
                  <div className="font-serif italic text-[#c2a978] text-5xl lg:text-6xl leading-none">
                    {c.num}
                  </div>
                </div>
                <div className="col-span-9 lg:col-span-10 pt-2">
                  <h3 className="font-serif text-2xl lg:text-3xl text-[#f5f5f0] leading-tight mb-4">
                    {c.title}
                  </h3>
                  <p className="text-[#8a8a8a] text-sm lg:text-base leading-relaxed max-w-xl">
                    {c.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
